"use client";

import React, { useContext, useMemo, useState } from "react";
import styles from "./MobileBanner.module.scss";
import { AppContext } from "../../context/AppContext";
import {
  COUPON_BONUS1_DEFAULT,
  COUPON_BONUS2_DEFAULT,
  COUPON_BONUS3_DEFAULT,
  COUPON_BONUS4_DEFAULT,
  COUPON_BONUS1_OPTIMIST,
  COUPON_BONUS2_OPTIMIST,
  COUPON_BONUS3_OPTIMIST,
  COUPON_BONUS4_OPTIMIST,
  COUPON_BONUS1_ELITE,
  COUPON_BONUS2_ELITE,
  COUPON_BONUS3_ELITE,
  COUPON_BONUS4_ELITE,
} from "../../data/couponSets";

// prilagodi putanje po projektu
import CouponInfoModal from "../couponInfoModal/CouponInfoModal";
import CouponErrorModal from "../couponErrorModal/CouponErrorModal";
import { ActivateCoupon } from "../../services/coupon";
import NewGamePromoBanner from "../NewGamePromoBanner/NewGamePromoBanner";
import NflWeek1Banner from "../NflWeek1Banner/NflWeek1Banner";

type CouponConfig = {
  title: string;
  bonus: string;
  mco?: string;
  minDeposit?: string;
  wagering?: string;
  maxBet?: string;
  maxBonus?: string;
  games?: string;
  available?: string[];
  code?: string;
  infoText?: string;
  ctaHref: string;
  mainValue?: string;
};

type SetKey = "default" | "nondep" | "optimist" | "elite";

const GRID_BY_SET: Record<SetKey, CouponConfig[]> = {
  // HOME + NON-DEP dele iste 4 kupona
  default: [
    COUPON_BONUS1_DEFAULT,
    COUPON_BONUS2_DEFAULT,
    COUPON_BONUS3_DEFAULT,
    COUPON_BONUS4_DEFAULT,
  ],
  nondep: [
    COUPON_BONUS1_DEFAULT,
    COUPON_BONUS2_DEFAULT,
    COUPON_BONUS3_DEFAULT,
    COUPON_BONUS4_DEFAULT,
  ],

  // Bronze Tail / Silver
  optimist: [
    COUPON_BONUS1_OPTIMIST,
    COUPON_BONUS2_OPTIMIST,
    COUPON_BONUS3_OPTIMIST,
    COUPON_BONUS4_OPTIMIST,
  ],

  // VIP Bronze Tail / Transferred
  elite: [
    COUPON_BONUS1_ELITE,
    COUPON_BONUS2_ELITE,
    COUPON_BONUS3_ELITE,
    COUPON_BONUS4_ELITE,
  ],
};

function mapPlayerClass(rawClass?: string): SetKey {
  if (!rawClass) return "default";

  const normalized = rawClass.toLowerCase().trim();

  if (normalized.includes("non") && normalized.includes("dep")) {
    return "nondep";
  }
  if (normalized.includes("bronze tail") || normalized.includes("silver")) {
    return "optimist";
  }
  if (normalized.includes("vip")) {
    return "elite";
  }

  return "default";
}

type CouponErrState = {
  open: boolean;
  status: number | null;
  message: string;
};

export default function MobileBanner() {
  const {
    user,
    setPageLoader,
    setActiveCoupon,
    setIsCouponActivated,
    setIsClosed,
  } = useContext(AppContext);

  const [infoCoupon, setInfoCoupon] = useState<CouponConfig | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [couponErr, setCouponErr] = useState<CouponErrState>({
    open: false,
    status: null,
    message: "",
  });

  const coupons = useMemo(() => {
    const key = mapPlayerClass(user?.playerClass);
    return GRID_BY_SET[key] || GRID_BY_SET.default;
  }, [user]);

  // ===============================
  //  Redeem logika (isti pattern kao u NewHomeStyle)
  // ===============================
  async function handleCouponRedeem(
    e: React.MouseEvent<
      HTMLAnchorElement | HTMLDivElement | HTMLButtonElement,
      MouseEvent
    >,
    couponCode?: string
  ) {
    e.preventDefault();
    e.stopPropagation();

    if (!couponCode) return;

    // 1) Nema usera -> otvori auth modal (login tab)
    if (!user) {
      setIsClosed?.(false);
      return;
    }

    // 2) Ima usera -> activate coupon
    if (busyCode) return;

    try {
      setBusyCode(couponCode);
      setPageLoader?.(true);

      const resp = await ActivateCoupon({ cuponCode: couponCode });

      if (resp !== 204 && Array.isArray(resp)) {
        const next = resp[0];
        setActiveCoupon?.(next);
        setIsCouponActivated?.(next?.bank_method_list ? false : true);

        setCouponErr({
          open: true,
          status: 200,
          message: `Coupon ${couponCode} successfully redeemed!`,
        });
      } else {
        // 204 – treat as OK
        setIsCouponActivated?.(true);
        setCouponErr({
          open: true,
          status: 200,
          message: `Coupon ${couponCode} activated.`,
        });
      }
    } catch (err: any) {
      const status = err?.response?.status ?? null;
      const msg =
        err?.response?.data?.message ||
        err.message ||
        "Redeem failed.";
      setCouponErr({ open: true, status, message: msg });
    } finally {
      setBusyCode(null);
      setPageLoader?.(false);
    }
  }

  return (
    <>
      <div className={styles.wrapper}>
        {!user && (
          <div className={styles.grid}>
            {coupons.map((coupon) => (
              <div
                key={coupon.code || coupon.title}
                className={styles.card}
                onClick={() => setInfoCoupon(coupon)}
              >
                <div className={styles.cardTitle}>{coupon.title}</div>
                <div className={styles.cardBonus}>{coupon.bonus}</div>
                {/* INFO (i) BUTTON – KAO NA MRO */}
                <button
                  type="button"
                  className={styles.cardInfo}
                  aria-label="More info"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setInfoCoupon(coupon);
                  }}
                />

                {coupon.minDeposit && (
                  <div className={styles.cardMeta}>
                    Min dep: <span>{coupon.minDeposit}</span>
                  </div>
                )}

                {/* {coupon.code && (
                <div className={styles.cardCode}>
                  Code: <span>{coupon.code}</span>
                </div>
              )} */}

                <a
                  href={coupon.ctaHref}
                  className={styles.cardCta}
                  onClick={(e) => handleCouponRedeem(e, coupon.code)}
                >
                  {user
                    ? busyCode === coupon.code
                      ? "Applying…"
                      : "Get Now"
                    : "Claim Now"}
                </a>
              </div>
            ))}
          </div>
        )}
        {/* <NflWeek1Banner placement="mobile" /> */}
        <NewGamePromoBanner />
      </div>

      {/* Modali – ista logika kao na MRO */}
      <CouponInfoModal
        open={!!infoCoupon}
        onClose={() => setInfoCoupon(null)}
        coupon={infoCoupon}
      />
      <CouponErrorModal
        open={couponErr.open}
        onClose={() =>
          setCouponErr({ open: false, status: null, message: "" })
        }
        statusCode={couponErr.status}
        genericMessage={couponErr.message}
      />
    </>
  );
}
